'use client'

import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

const OPTIONS = [
  { value: 'light' as const, label: 'Light' },
  { value: 'dark' as const, label: 'Dark' },
  { value: 'system' as const, label: 'System' },
]

export type ThemeModeSelectProps = {
  /** Default: sidebar-style panel. Marketing: light text on accent footer. */
  variant?: 'default' | 'marketing'
  className?: string
  /** Section label (default only). */
  sectionLabel?: string
}

export function ThemeModeSelect({
  variant = 'default',
  className,
  sectionLabel = 'Appearance',
}: ThemeModeSelectProps) {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  if (!mounted) {
    return (
      <div
        className={cn(
          variant === 'marketing' ? 'h-6 w-48 rounded bg-white/10' : 'h-[88px] w-full rounded-xl bg-neutral-100 dark:bg-neutral-800',
          className
        )}
        aria-hidden
      />
    )
  }

  const value = theme ?? 'system'

  if (variant === 'marketing') {
    return (
      <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', className)}>
        <span className="text-xs font-semibold uppercase tracking-widest text-white/50">Theme</span>
        <RadioGroup
          value={value}
          onValueChange={(v) => setTheme(v)}
          className="flex flex-wrap items-center gap-4"
        >
          {OPTIONS.map(({ value: v, label }) => (
            <div key={v} className="flex items-center gap-2">
              <RadioGroupItem
                value={v}
                id={`theme-${v}-marketing`}
                className="border-white/50 text-white ring-offset-accent-600 focus-visible:ring-white/40"
              />
              <Label htmlFor={`theme-${v}-marketing`} className="mb-0 cursor-pointer text-sm font-normal text-white/85">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    )
  }

  return (
    <div className={cn('border-b border-atrium-border px-3 py-3', className)}>
      <p className="mb-2 text-xs font-normal text-atrium-black/70">{sectionLabel}</p>
      <RadioGroup value={value} onValueChange={(v) => setTheme(v)} className="grid gap-2">
        {OPTIONS.map(({ value: v, label }) => (
          <div key={v} className="flex items-center gap-2">
            <RadioGroupItem
              value={v}
              id={`theme-${v}`}
              className="dark:border-atrium-border-dark dark:text-atrium-black dark:ring-offset-atrium-background"
            />
            <Label htmlFor={`theme-${v}`} className="mb-0 cursor-pointer text-sm font-normal leading-snug text-atrium-black">
              {label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  )
}
